// Domain types shared across the app — CONTRACT FILE (director-owned).
// Database row types live in src/lib/db/schema.ts; import those from there.

export const ROLES = ["investor", "consultant", "admin"] as const;
export type Role = (typeof ROLES)[number];

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

export const SPECIALTIES = [
  "public_pension",
  "corporate_db",
  "endowment",
  "foundation",
  "taft_hartley",
  "healthcare",
  "defined_contribution",
  "insurance",
  "other",
] as const;
export type Specialty = (typeof SPECIALTIES)[number];

export const SPECIALTY_LABELS: Record<Specialty, string> = {
  public_pension: "Public pension",
  corporate_db: "Corporate DB",
  endowment: "Endowment",
  foundation: "Foundation",
  taft_hartley: "Taft-Hartley",
  healthcare: "Healthcare",
  defined_contribution: "Defined contribution",
  insurance: "Insurance",
  other: "Other",
};

export const SERVICES = ["advisory", "ocio", "both"] as const;
export type FirmService = (typeof SERVICES)[number];

/**
 * Client-safe mirror of orgTypeEnum in src/lib/db/schema.ts — client
 * components import THIS list, never the Drizzle schema. Kept in sync by
 * tests/contracts/org-types.test.ts.
 */
export const ORG_TYPES = [
  "corporate_db",
  "public_pension",
  "endowment",
  "foundation",
  "taft_hartley",
  "healthcare",
  "other",
] as const;
export type OrgType = (typeof ORG_TYPES)[number];

export const SERVICE_LABELS: Record<FirmService, string> = {
  advisory: "Advisory",
  ocio: "OCIO",
  both: "Advisory + OCIO",
};

/** One section of an RFP document (rfps.sections / responses.sections jsonb). */
export type RfpSection = {
  key: string;
  title: string;
  content: string;
  /**
   * True if any part of this section's content came from the AI assist.
   * Compliance audit trail (Regla No Negociable #2) — never strip this flag.
   */
  ai_assisted: boolean;
};

/**
 * Structured requirements captured in the RFP builder. Drives the
 * TRANSPARENT recipient suggestions (see src/lib/rfps/suggest.ts).
 */
export type RfpCriteria = {
  /** Firm specialties must overlap ≥1 when non-empty. */
  specialties: Specialty[];
  /** null = no service filter. */
  service: FirmService | null;
  /** Firm HQ state must be in this list when non-empty (2-letter codes). */
  states: string[];
  /** Firms with unknown AUA are excluded when this is set. */
  min_aua_billions: number | null;
  /** Investor's own plan size — context for AI drafting, NEVER a firm filter. */
  aum_range: string | null;
  notes: string | null;
};

export const EMPTY_CRITERIA: RfpCriteria = {
  specialties: [],
  service: null,
  states: [],
  min_aua_billions: null,
  aum_range: null,
  notes: null,
};

/** Stored in responses.attachments jsonb; `path` is the Supabase Storage object path. */
export type ResponseAttachment = {
  path: string;
  name: string;
  size: number;
};
